import { useCallback, useRef, useState } from "react";

import { TAB_DRAG_THRESHOLD_PX } from "../constants";

export type DropTarget = "left" | "right" | "detach" | null;

interface DropZoneRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseTabDragOptions {
  onSelect: () => void;
  onDragToSplit: (side: "left" | "right") => void;
  onDragOut: () => void;
  onDragStateChange: (isDragging: boolean, dropTarget: DropTarget) => void;
  dropZoneRect: DropZoneRect | null;
  /** Whether this tab is already in a split. If true, dropping in the center exits split. */
  isInSplit?: boolean;
  onExitSplit?: () => void;
}

export interface UseTabDragResult {
  handlePointerDown: (event: React.PointerEvent) => void;
  handlePointerMove: (event: React.PointerEvent) => void;
  handlePointerUp: (event: React.PointerEvent) => void;
  isDragging: boolean;
  dropTarget: DropTarget;
  dragPosition: { x: number; y: number } | null;
}

export function useTabDrag({
  onSelect,
  onDragToSplit,
  onDragOut,
  onDragStateChange,
  dropZoneRect,
  isInSplit = false,
  onExitSplit,
}: UseTabDragOptions): UseTabDragResult {
  const [isDragging, setIsDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState<DropTarget>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const hasStartedDrag = useRef(false);

  const computeDropTarget = useCallback(
    (clientX: number, clientY: number): DropTarget => {
      if (!dropZoneRect) {
        return null;
      }

      const isInsideRect =
        clientX >= dropZoneRect.x &&
        clientX <= dropZoneRect.x + dropZoneRect.width &&
        clientY >= dropZoneRect.y &&
        clientY <= dropZoneRect.y + dropZoneRect.height;

      if (!isInsideRect) {
        return "detach";
      }

      const midX = dropZoneRect.x + dropZoneRect.width / 2;
      return clientX < midX ? "left" : "right";
    },
    [dropZoneRect]
  );

  const handlePointerDown = useCallback((event: React.PointerEvent) => {
    startPos.current = { x: event.clientX, y: event.clientY };
    hasStartedDrag.current = false;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!startPos.current) {
        return;
      }

      const dx = event.clientX - startPos.current.x;
      const dy = event.clientY - startPos.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (!hasStartedDrag.current && distance > TAB_DRAG_THRESHOLD_PX) {
        hasStartedDrag.current = true;
        setIsDragging(true);
      }

      if (hasStartedDrag.current) {
        const target = computeDropTarget(event.clientX, event.clientY);
        setDropTarget(target);
        setDragPosition({ x: event.clientX, y: event.clientY });
        onDragStateChange(true, target);
      }
    },
    [computeDropTarget, onDragStateChange]
  );

  const handlePointerUp = useCallback(() => {
    if (!hasStartedDrag.current) {
      onSelect();
    } else if (dropTarget === "detach") {
      onDragOut();
    } else if (dropTarget === "left" || dropTarget === "right") {
      // If already in split and dropped on same side, exit split
      if (isInSplit && onExitSplit) {
        onExitSplit();
      } else {
        onDragToSplit(dropTarget);
      }
    }

    startPos.current = null;
    hasStartedDrag.current = false;
    setIsDragging(false);
    setDropTarget(null);
    setDragPosition(null);
    onDragStateChange(false, null);
  }, [dropTarget, onSelect, onDragToSplit, onDragOut, onDragStateChange, isInSplit, onExitSplit]);

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    isDragging,
    dropTarget,
    dragPosition,
  };
}
